import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Input, Modal, Space, Table, Tag, Tooltip, message } from 'antd';
import { ChargeForm, UNIT_TEXT, errorText, plainUnit } from '../../components/billing/ChargeForm';
import {
  createNonStdChargeLine,
  getNonStdApplicableRules,
  getNonStdPriceHint,
  updateNonStdChargeLine,
  voidNonStdChargeLine,
} from '../../services/nonstandardOps';
import {
  NONSTD_RULE_LEVEL_TEXT,
  NonStdApplicableRule,
  NonStdChargeLine,
} from '../../types/nonstandardOps';

const TIER_BASIS_TEXT: Record<string, [string, string]> = {
  QUANTITY: ['数量', ''], WEIGHT_G: ['重量', '克'], WEIGHT_KG: ['重量', '千克'], VOLUME_CUFT: ['体积', '立方英尺'], VOLUME_CBM: ['体积', '立方米'], VOLUME_CC: ['体积', '立方厘米'],
};
const LEVEL_COLOR: Record<string, string> = { project: 'purple', customer: 'blue', system: 'cyan' };

/** 数量后面的单位：优先展示单位，没有就用计费单位的文字 */
const unitOf = (line: Pick<NonStdChargeLine, 'billing_unit' | 'unit_label'>) => plainUnit(line.unit_label) || UNIT_TEXT[line.billing_unit] || '';

interface Props {
  instructionId: number;
  lines: NonStdChargeLine[];
  totals: Record<string, string>;
  /** 管理员：可以看到价格来源和状态，并且（canEdit 时）管理收费行；客户只读 */
  isAdmin: boolean;
  /** 指令状态允许修改，且当前页面是终审页 */
  canEdit: boolean;
  onChanged: (opts?: { requiresRecalc?: boolean }) => void;
}

const ChargeLinesPanel: React.FC<Props> = ({ instructionId, lines, totals, isAdmin, canEdit, onChanged }) => {
  const [editing, setEditing] = useState<NonStdChargeLine | null>(null);
  const [rules, setRules] = useState<NonStdApplicableRule[] | null>(null);
  const canManage = isAdmin && canEdit;
  const fetchHint = useCallback(
    (params: { rule_id: number; quantity: number; tier_value?: number }) => getNonStdPriceHint({ instruction: instructionId, ...params }),
    [instructionId],
  );

  useEffect(() => {
    if (!canManage) return;
    let alive = true;
    getNonStdApplicableRules(instructionId)
      .then(list => { if (alive) setRules(list); })
      .catch(err => { if (alive) { setRules([]); message.error(errorText(err, '加载收费规则失败，可以先用“临时收费”')); } });
    // eslint-disable-next-line consistent-return
    return () => { alive = false; };
  }, [instructionId, canManage]);

  const handleVoid = (line: NonStdChargeLine) => {
    let reason = '';
    Modal.confirm({
      title: '作废这条收费行？',
      content: <Input placeholder="作废原因（选填）" onChange={e => { reason = e.target.value; }} />,
      okText: '作废',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        try {
          const res = await voidNonStdChargeLine(line.id, reason);
          message.success(res?.requires_recalc ? '已作废；这条已生成过收费记录，需要重新终审提交（重算）才会更新收费记录' : '已作废');
          onChanged({ requiresRecalc: !!res?.requires_recalc });
        } catch (err: any) {
          message.error(errorText(err, '作废失败'));
          throw err;
        }
      },
    });
  };

  const currencies = Array.from(new Set(lines.map(line => line.currency).filter(Boolean)));

  const columns = useMemo(() => {
    const base: any[] = [
      {
        title: '收费项目', key: 'item',
        render: (_: unknown, line: NonStdChargeLine) => (
          <div>
            <div>{line.description}</div>
            {line.rule_name && line.rule_name !== line.description ? <div style={{ fontSize: 12, color: '#8c8c8c' }}>规则：{line.rule_name}</div> : null}
            {line.tier_value != null && TIER_BASIS_TEXT[line.tier_basis || ''] ? (
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>按{TIER_BASIS_TEXT[line.tier_basis || ''][0]} {Number(line.tier_value)} {TIER_BASIS_TEXT[line.tier_basis || ''][1]}确定档位</div>
            ) : null}
          </div>
        ),
      },
    ];
    if (isAdmin) {
      base.push({
        title: '来源', key: 'source', width: 150,
        render: (_: unknown, line: NonStdChargeLine) => {
          if (line.is_temporary) return <Tooltip title={line.price_reason}><Tag color="orange">临时收费</Tag></Tooltip>;
          if (!line.rule_id) return <Tooltip title={line.price_reason}><Tag>旧版手填</Tag></Tooltip>;
          return (
            <Space size={4} wrap>
              <Tag color={LEVEL_COLOR[line.rule_level || ''] || 'green'}>{NONSTD_RULE_LEVEL_TEXT[line.rule_level || ''] || '规则'}</Tag>
              {line.price_source === 'override'
                ? <Tooltip title={`${line.price_reason}（规则价 ${line.rule_unit_price ?? '-'}）`}><Tag color="red">改价</Tag></Tooltip>
                : null}
            </Space>
          );
        },
      });
    }
    // 币种只有一种时写在列头（单价(USD) / 总价(USD)），格子里只放数字；多币种时列头不写，格子里带币种
    const unit = currencies.length === 1 ? `(${currencies[0]})` : '';
    const withCurrency = (value: string, currency: string) => (currencies.length === 1 ? value : `${value} ${currency}`);
    base.push(
      { title: `单价${unit}`, key: 'price', width: 110, render: (_: unknown, line: NonStdChargeLine) => withCurrency(line.unit_price, line.currency) },
      {
        title: '数量', key: 'qty', width: 100,
        render: (_: unknown, line: NonStdChargeLine) => (
          <div>
            <div>{Number(line.quantity)}</div>
            <div style={{ fontSize: 12, color: '#8c8c8c' }}>{unitOf(line)}</div>
          </div>
        ),
      },
      { title: `总价${unit}`, key: 'amount', width: 120, render: (_: unknown, line: NonStdChargeLine) => <b>{withCurrency(line.amount, line.currency)}</b> },
    );
    if (isAdmin && canEdit) {
      base.push({
        title: '操作', key: 'ops', width: 80,
        render: (_: unknown, line: NonStdChargeLine) => (
          <Space direction="vertical" size={4}>
            <a onClick={() => setEditing(line)}>编辑</a>
            <a style={{ color: '#cf1322' }} onClick={() => handleVoid(line)}>作废</a>
          </Space>
        ),
      });
    }
    return base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, canEdit, currencies.join(',')]);

  const totalEntries = Object.entries(totals);

  return (
    <div>
      <Table
        size="small"
        rowKey="id"
        pagination={false}
        dataSource={lines}
        columns={columns}
        locale={{ emptyText: '还没有收费行' }}
        summary={() => (lines.length > 0 ? (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={columns.length}>
              <Space size={8} wrap>
                <b>合计</b>
                {totalEntries.length === 0 ? '-' : totalEntries.map(([currency, amount]) => (
                  <span
                    key={currency}
                    className="nonstd-charge-total"
                    style={{ display: 'inline-block', padding: '2px 12px', borderRadius: 4, background: '#fff1f0', border: '1px solid #ffa39e', color: '#cf1322', fontWeight: 600 }}
                  >
                    {currency} {amount}
                  </span>
                ))}
              </Space>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        ) : null)}
      />
      {canManage ? (
        <div style={{ marginTop: 12, padding: 12, background: '#fafafa', borderRadius: 6 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>添加收费行</div>
          <ChargeForm
            key={lines.length}
            fetchHint={fetchHint}
            rules={rules}
            onSubmit={async payload => {
              const created = await createNonStdChargeLine(instructionId, payload);
              message.success('已添加');
              if (created.warning) message.warning(created.warning);
              onChanged();
            }}
          />
        </div>
      ) : null}
      <Modal title="编辑收费行" open={!!editing} onCancel={() => setEditing(null)} footer={null} destroyOnClose width={760}>
        {editing ? (
          <ChargeForm
            fetchHint={fetchHint}
            rules={rules}
            initial={editing}
            onCancel={() => setEditing(null)}
            onSubmit={async payload => {
              const updated = await updateNonStdChargeLine(editing.id, payload);
              message.success(updated.requires_recalc ? '已保存；这条已生成过收费记录，需要重新终审提交（重算）才会更新收费记录' : '已保存');
              if (updated.warning) message.warning(updated.warning);
              setEditing(null);
              onChanged({ requiresRecalc: !!updated.requires_recalc });
            }}
          />
        ) : null}
      </Modal>
    </div>
  );
};

export default ChargeLinesPanel;
